import React, { useEffect, useMemo, useState } from "react"
import { useApolloClient } from "@apollo/client"
import { Group, ManagerLoginPayload } from "../../generated-types"
import { defaultGroupId } from "../../contants/group"
import { setToken, getToken, clearToken } from "../../graphql"

interface UserInfo {
  name: string
  email: string
  authority: string
  isAdmin: boolean
  isDistributor: boolean
}

interface LoginContextType extends UserInfo {
  login: (payload: ManagerLoginPayload) => void
  logout: () => void
  isLogined: boolean
  groupId: string
  setGroupId: (groupId: string) => void
  realGroupId: number
  regionCode: string
  setRegionCode: (regionCode: string) => void
  groups: Group[]
  setGroups: (groups: Group[]) => void
}

export const LoginContext = React.createContext<LoginContextType | undefined>(undefined)
LoginContext.displayName = "LoginContext"

const convertGroupId = (groupId) => {
  return groupId === defaultGroupId ? undefined : Number(groupId)
}

export const LoginContextProvider: React.FC<React.PropsWithChildren> = (props) => {
  const client = useApolloClient()
  const [userInfo, setUserInfo] = useState<UserInfo>(undefined)
  const [groupId, setGroupId] = useState<string>(localStorage.getItem("groupId"))
  const [realGroupId, setRealGroupId] = useState<number>(convertGroupId(groupId))
  const [regionCode, setRegionCode] = useState<string>(localStorage.getItem("regionCode"))
  const [groups, setGroups] = useState<Group[]>()

  // localStorage에서 세션 복원 (새 창/새로고침 시)
  useEffect(() => {
    if (!getToken() && localStorage.getItem("username")) {
      localStorage.removeItem("token"); localStorage.removeItem("groupId"); localStorage.removeItem("regionCode"); localStorage.removeItem("username"); localStorage.removeItem("authority")
    } else if (getToken() && !userInfo && localStorage.getItem("username")) {
      const authority = localStorage.getItem("authority") || ""
      setUserInfo({
        name: localStorage.getItem("username"),
        email: "",
        authority,
        isAdmin: authority === "SUPER_ADMIN",
        isDistributor: authority === "SUPER_ADMIN" || authority === "DISTRIBUTOR",
      })
    }
  }, [])

  useEffect(() => {
    setRealGroupId(convertGroupId(groupId))
    localStorage.setItem("groupId", groupId)
  }, [groupId])

  useEffect(() => {
    localStorage.setItem("regionCode", regionCode)
  }, [regionCode])

  const ctx = useMemo(
    () => ({
      login: (payload: ManagerLoginPayload) => {
        const { username, authority, regionCode, token } = payload
        const newUser = {
          name: username,
          email: "",
          authority,
          isAdmin: authority === "SUPER_ADMIN",
          isDistributor: authority === "SUPER_ADMIN" || authority === "DISTRIBUTOR",
        }

        setToken(token)

        setGroupId(defaultGroupId)
        setRealGroupId(convertGroupId(groupId))
        setRegionCode(regionCode)

        localStorage.setItem("groupId", defaultGroupId)
        localStorage.setItem("regionCode", regionCode)
        localStorage.setItem("username", username)
        localStorage.setItem("authority", authority)
        setUserInfo(newUser)
      },
      logout: () => {
        clearToken()
        localStorage.removeItem("token"); localStorage.removeItem("groupId"); localStorage.removeItem("regionCode"); localStorage.removeItem("username"); localStorage.removeItem("authority")
        setUserInfo(undefined)
        client.cache.reset()
      },
      isLogined: !!getToken() && !!userInfo,
      ...userInfo,
    }),
    [userInfo]
  )
  return (
    <LoginContext.Provider
      value={{
        ...ctx,
        groupId,
        setGroupId,
        realGroupId,
        regionCode,
        setRegionCode,
        groups,
        setGroups,
      }}
    >
      {props.children}
    </LoginContext.Provider>
  )
}

export const useLoginContext = () => React.useContext<LoginContextType>(LoginContext)
